import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNullableTokens } from '@/common/providers/tokens-provider';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

type UseLogoutActionData = {
    logout: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Signing out. The local tokens go either way: a refresh token the server has
 * already forgotten is still one this browser should stop holding.
 */
export function useLogoutAction(): UseLogoutActionData {
    const { t } = useMeTranslation();
    const { tokens, deleteTokens } = useNullableTokens();
    const { openApi } = useOpenApi();

    const getErrorMessage = useCallback(
        (error: unknown): string => t(getFallbackErrorKey(error)),
        [t]
    );

    const mutation = useMutation({
        mutationFn: () => {
            if (!tokens?.refreshToken) {
                return Promise.resolve();
            }

            return openApi.auth
                .logout({ refreshToken: tokens.refreshToken })
                .then(() => {});
        },
        onSuccess: () => {
            deleteTokens();
        },
        onError: (error) => {
            // The session is gone server-side, which is the state we wanted.
            if (isApiError(error, 'UNAUTHORISED')) {
                deleteTokens();
                return;
            }

            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        logout: () =>
            mutation.mutateAsync().catch((error: unknown) => {
                // An already-dead session is not a failure the caller needs.
                if (isApiError(error, 'UNAUTHORISED')) return;
                throw error;
            }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
